// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectOrg } from '../../selectors/selectors';
import { OrgComponentProps, AIProviderType, AllAIProviderTypes } from '../../typescriptHelpers';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateOrgSettings, UpdateAISettingsRequest } from '../../actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Divider from 'antd/lib/divider';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Switch from 'antd/lib/switch';
import { EditOutlined } from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';

const displayableNameForAIProvider = (provider: AIProviderType): string => {
    switch (provider) {
        case AIProviderType.OpenAI:
            return 'OpenAI';
        case AIProviderType.Claude:
            return 'Claude';
    }
};

const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    flex: 1,
    minHeight: 80,
};

const AISettings: React.FC<OrgComponentProps> = (props) => {
    const dispatch = useDispatch();
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));

    const isLoadingToggle = useIsLoading(OrgSettingsUpdatable.UpdateAISettings, 'toggle');
    const isLoadingProvider = useIsLoading(OrgSettingsUpdatable.UpdateAISettings, 'provider');
    const isLoadingByokSave = useIsLoading(OrgSettingsUpdatable.UpdateAISettings, 'byok-save');
    const isLoadingByokRemove = useIsLoading(OrgSettingsUpdatable.UpdateAISettings, 'byok-remove');

    const providerUpdated = useUpdated(OrgSettingsUpdatable.UpdateAISettings, 'provider');
    const byokSaveUpdated = useUpdated(OrgSettingsUpdatable.UpdateAISettings, 'byok-save');

    const error = useError(OrgSettingsUpdatable.UpdateAISettings);

    const [providerModalOpen, setProviderModalOpen] = React.useState(false);
    const [providerForm] = Form.useForm();

    const [byokModalOpen, setByokModalOpen] = React.useState(false);
    const [byokForm] = Form.useForm();

    React.useEffect(() => {
        if (providerUpdated) {
            globalMessage.success('AI setting updated!');
            setProviderModalOpen(false);
        }
    }, [providerUpdated]);

    React.useEffect(() => {
        if (byokSaveUpdated) {
            globalMessage.success('AI setting updated!');
            setByokModalOpen(false);
        }
    }, [byokSaveUpdated]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    if (!org) return null;

    const aiEnabled = !org.aiGloballyDisabled;
    const aiProvider = org.aiProvider;
    const hasBYOK = org.aiHasBYOKForProvider?.[aiProvider] ?? false;

    const dispatchUpdate = (update: Omit<UpdateAISettingsRequest, 'orgId'>, id: string) => {
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateAISettings, { orgId: props.orgId, ...update }, id));
    };

    const onSaveProvider = (values: { aiProvider: AIProviderType }) => {
        dispatchUpdate({ aiProvider: values.aiProvider }, 'provider');
    };

    const onSaveByok = (values: { apiKey: string }) => {
        dispatchUpdate({ byokApiKey: values.apiKey }, 'byok-save');
    };

    const onRemoveByok = () => {
        dispatchUpdate({ byokApiKey: '' }, 'byok-remove');
    };

    return (
        <Content>
            <div style={{ maxWidth: 900, width: '100%', marginTop: 12 }}>
                <Text style={{ display: 'block', marginBottom: 16 }}>
                    Manage AI related settings. Runway never trains or allows third party providers to train models
                    using customer data.
                </Text>

                {/* Global AI toggle */}
                <div style={rowStyle}>
                    <Space direction="vertical" size={4}>
                        <Text strong>AI features</Text>
                        <Text type="secondary">Enable or disable all AI functionality for your organization</Text>
                    </Space>
                    <Switch
                        checked={aiEnabled}
                        loading={isLoadingToggle}
                        onChange={(checked) => dispatchUpdate({ aiEnabled: checked }, 'toggle')}
                    />
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* AI Provider row */}
                <div style={rowStyle}>
                    <Space direction="vertical" size={4}>
                        <Text>
                            <Text strong>AI provider:</Text> {displayableNameForAIProvider(aiProvider)}
                        </Text>
                        <Text type="secondary">Select a specific provider to use for AI functionality</Text>
                    </Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            providerForm.setFieldsValue({ aiProvider: aiProvider });
                            setProviderModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* BYOK row */}
                <div style={rowStyle}>
                    <Space direction="vertical" size={4}>
                        <Text>
                            <Text strong>Bring-your-own API key:</Text>{' '}
                            {hasBYOK ?
                                <Text style={{ letterSpacing: 2 }}>••••••••</Text>
                            :   <Text type="secondary">Not set</Text>}
                        </Text>
                        <Text type="secondary">Optionally use your own API key for the selected AI provider</Text>
                    </Space>
                    <Space size="small">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                byokForm.resetFields();
                                setByokModalOpen(true);
                            }}
                        >
                            {hasBYOK ? 'Replace' : 'Add key'}
                        </Button>
                        {hasBYOK && (
                            <Button
                                type="link"
                                size="small"
                                danger
                                loading={isLoadingByokRemove}
                                onClick={onRemoveByok}
                            >
                                Remove
                            </Button>
                        )}
                    </Space>
                </div>
            </div>

            {/* AI Provider modal */}
            <Modal
                title="Edit AI provider"
                open={providerModalOpen}
                okText="Save"
                okButtonProps={{ loading: isLoadingProvider }}
                onOk={() => providerForm.submit()}
                onCancel={() => setProviderModalOpen(false)}
                afterClose={() => providerForm.resetFields()}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text>Select which AI provider Runway will use for all AI-powered features.</Text>
                    <Form
                        form={providerForm}
                        layout="vertical"
                        onFinish={onSaveProvider}
                        style={{ marginTop: 24, width: '100%' }}
                    >
                        <Form.Item name="aiProvider" label="AI provider">
                            <Select style={{ width: 200 }}>
                                {AllAIProviderTypes.map((p) => (
                                    <Select.Option key={p} value={p}>
                                        {displayableNameForAIProvider(p)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>

            {/* BYOK modal */}
            <Modal
                title={`${hasBYOK ? 'Replace' : 'Add'} ${displayableNameForAIProvider(aiProvider)} API key`}
                open={byokModalOpen}
                okText="Save"
                okButtonProps={{ loading: isLoadingByokSave }}
                onOk={() => byokForm.submit()}
                onCancel={() => setByokModalOpen(false)}
                afterClose={() => byokForm.resetFields()}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text>
                        Optionally provide your own {displayableNameForAIProvider(aiProvider)} API key to use for all AI
                        functionality.
                    </Text>
                    <Form
                        form={byokForm}
                        layout="vertical"
                        onFinish={onSaveByok}
                        style={{ marginTop: 24, width: '100%' }}
                    >
                        <Form.Item
                            name="apiKey"
                            label={`${displayableNameForAIProvider(aiProvider)} API key`}
                            rules={[{ required: true, message: 'Please enter an API key' }]}
                        >
                            <Input
                                autoFocus
                                style={{ width: '100%', WebkitTextSecurity: 'disc' } as React.CSSProperties}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
        </Content>
    );
};

export default AISettings;
